import { useGetLeagueById, useGetLeagueUsers } from '@/api/query/useLeagues';
import { toLeagueId, toUserId } from '@/utils/id-helpers';
import type { LeagueUser } from '@bet99-pickem/api-types';
import type { League, LeagueMember } from '../types/user';

export type ApiLeagueResponse = {
  league_id?: string;
  id?: string;
  title: string;
  current_members?: number;
  creator_id?: string;
  owner_id?: string;
  league_code?: string;
  code?: string;
  is_public?: boolean;
  is_owner?: boolean;
};

// Map API League to UI League
function transformLeague(apiLeague: ApiLeagueResponse, index: number): League {
  return {
    id: toLeagueId(apiLeague.league_id ?? apiLeague.id),
    isOwner: apiLeague.is_owner ?? false,
    name: apiLeague.title,
    color: index % 2 === 0 ? 'orange' : 'blue',
    memberCount: apiLeague.current_members ?? 0,
    members: [],
    createdBy: toUserId(apiLeague.creator_id ?? apiLeague.owner_id),
    code: apiLeague.league_code ?? apiLeague.code,
    isPublic: apiLeague.is_public ?? false,
  };
}

// Map API LeagueUser to UI LeagueMember
function transformLeagueUser(user: LeagueUser): LeagueMember {
  return {
    id: toUserId(user.user_id),
    username: user.username,
    isCreator: user.is_owner,
    points: user.points,
    rank: user.rank,
  };
}

export const useLeagueDetails = (leagueId: string) => {
  const leagueQuery = useGetLeagueById(leagueId);
  const membersQuery = useGetLeagueUsers(leagueId);

  // API may return either {users: [...]} or [...] directly
  type MembersResponse = { users?: unknown[] } | unknown[];
  const membersData = membersQuery.data as MembersResponse;
  const membersArray =
    membersData && typeof membersData === 'object' && 'users' in membersData
      ? (membersData.users ?? [])
      : Array.isArray(membersData)
        ? membersData
        : [];

  // API may return either {league: {...}} or {...} directly
  type LeagueResponse = { league?: unknown } | unknown;
  const leagueData = leagueQuery.data as LeagueResponse;
  const leagueObject =
    leagueData && typeof leagueData === 'object' && 'league' in leagueData
      ? leagueData.league
      : leagueData;

  const league = leagueObject ? transformLeague(leagueObject as ApiLeagueResponse, 0) : undefined;

  if (league) {
    league.members = membersArray.map(transformLeagueUser);
  }

  return {
    league,
    isLoading: leagueQuery.isLoading || membersQuery.isLoading,
    error: leagueQuery.error || membersQuery.error,
    refetch: () => {
      leagueQuery.refetch();
      membersQuery.refetch();
    },
  };
};
